import { get, toPath } from "lodash";
import { useCallback, useEffect } from "react";
import { useFormState } from "react-final-form";
import { useAppDispatch } from "store/hooks";
import { EditedElement } from "../model";
import {
  changeFieldDefinitionProp,
  changeLayoutElementProp,
  renameColumnElement,
  renameFieldElement,
} from "../store/editorSlice";

const AutoSave = () => {
  const dispatch = useAppDispatch();
  const { values, dirty, dirtyFields } = useFormState<EditedElement>({
    subscription: {
      values: true,
      dirty: true,
      dirtyFields: true,
    },
  });

  const isLayoutProp = useCallback((prop: string) => {
    return prop.startsWith("element");
  }, []);

  const isSchemaProp = useCallback((prop: string) => {
    return prop.startsWith("fieldDef");
  }, []);

  /**
   * const dirtyFields = {
          "element": true,
          "element.listfields.fields.0": true,
          "element.listFields.fields.0.name": true,
      }
   * 
   */

  const handleSubmit = useCallback(() => {
    Object.keys(dirtyFields).forEach((targetProp) => {
      const isValidProp = !Object.keys(dirtyFields).some(
        (prop) => prop !== targetProp && prop.startsWith(targetProp)
      );

      if (isValidProp) {
        const value = get(values, targetProp);

        // Remove the 'element' or 'fieldDef' suffix in the property to distinguish if
        // is a schema property or a layout property
        // Example 1: element.listFields.fields.0.label -> listFields.fields.0.label
        // Example 2: fieldDef.required -> required
        const prop = toPath(targetProp).slice(1).join(".");

        const isFieldNameChanged = prop === "name";
        const isTableColumnNameChanged =
          prop.startsWith("listFields") && targetProp.endsWith("name");

        if (isFieldNameChanged) {
          dispatch(renameFieldElement({ targetProp: prop, value }));
          return;
        }
        if (isTableColumnNameChanged) {
          dispatch(renameColumnElement({ targetProp: prop, value }));
          return;
        }
        if (isLayoutProp(targetProp)) {
          dispatch(changeLayoutElementProp({ targetProp: prop, value }));
          return;
        }
        if (isSchemaProp(targetProp)) {
          dispatch(changeFieldDefinitionProp({ targetProp: prop, value }));
          return;
        }
      }
    });
  }, [dirtyFields, dispatch, isLayoutProp, isSchemaProp, values]);

  useEffect(() => {
    if (dirty) {
      handleSubmit();
    }
  }, [dirty, dirtyFields, dispatch, handleSubmit, values]);

  return null;
};

export default AutoSave;
